import { z } from 'zod';

import { commandSchema, rejectionReasonSchema } from './commands.js';
import type { GameProjection } from './projection.js';

export const SOCKET_PATH = '/realtime';

export const seatSchema = z.object({
  playerId: z.string(),
  name: z.string(),
  seat: z.number().int().min(0),
  isHost: z.boolean(),
  connected: z.boolean(),
});
export type Seat = z.infer<typeof seatSchema>;

export const lobbySchema = z.object({
  roomId: z.string(),
  status: z.enum(['lobby', 'playing', 'finished']),
  locked: z.boolean(),
  hostPlayerId: z.string(),
  seats: z.array(seatSchema),
  maxPlayers: z.number().int(),
  minPlayers: z.number().int(),
  /** Only sent to the host, and never written to logs. */
  inviteUrl: z.string().optional(),
});
export type Lobby = z.infer<typeof lobbySchema>;

/** Client -> server. */
export const attachSchema = z.object({
  roomId: z.string().min(1),
  resumeToken: z.string().min(1),
});
export type Attach = z.infer<typeof attachSchema>;

export const hostCommandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('startMatch') }),
  z.object({ type: z.literal('setLocked'), locked: z.boolean() }),
  z.object({ type: z.literal('removeSeat'), playerId: z.string().min(1) }),
]);
export type HostCommand = z.infer<typeof hostCommandSchema>;

export const clientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('attach'), payload: attachSchema }),
  z.object({ type: z.literal('command'), payload: commandSchema }),
  z.object({ type: z.literal('host'), payload: hostCommandSchema }),
]);
export type ClientMessage = z.infer<typeof clientMessageSchema>;

export const commandAckSchema = z.union([
  z.object({
    ok: z.literal(true),
    idempotencyKey: z.string(),
    version: z.number().int(),
    /** True when the key was already applied and nothing new happened. */
    duplicate: z.boolean(),
  }),
  z.object({
    ok: z.literal(false),
    idempotencyKey: z.string(),
    reason: rejectionReasonSchema,
    version: z.number().int(),
  }),
]);
export type CommandAck = z.infer<typeof commandAckSchema>;

/** Server -> client. Projections are typed but not re-validated on the wire. */
export interface ServerEvents {
  attached: (payload: { playerId: string; lobby: Lobby }) => void;
  lobby: (payload: Lobby) => void;
  state: (payload: GameProjection) => void;
  ack: (payload: CommandAck) => void;
  error: (payload: { message: string; code?: string }) => void;
}

export const SOCKET_EVENTS = ['attached', 'lobby', 'state', 'ack', 'error'] as const;
